import React, { useEffect, useState, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { db } from '../config';
import { doc, getDoc } from 'firebase/firestore';
import {
  FaPlay,
  FaPause,
  FaBook,
  FaUser,
  FaClock,
  FaStore,
  FaBackward,
  FaForward
} from 'react-icons/fa';

const APP_STORE_URL = 'https://apps.apple.com/app/id6744862510';

const OpenInApp = () => {
  const { id } = useParams();
  const location = useLocation();
  const isListen = location.pathname.startsWith('/listen');
  const scheme = isListen ? `estikz://listen/${id}` : `estikz://book/${id}`;

  const [bookData, setBookData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isBuffering, setIsBuffering] = useState(false);
  const [audioError, setAudioError] = useState(false);
  const audioRef = useRef(null);

  const isErTostik = id === 'er_tostik';

  useEffect(() => {
    const fetchBookData = async () => {
      setLoading(true);
      try {
        const bookRef = doc(db, 'books', id);
        const bookSnap = await getDoc(bookRef);
        if (bookSnap.exists()) {
          const data = bookSnap.data();
          setBookData(data);

          // Аудио файлды жүктеу (тек er_tostik үшін)
          if (isErTostik && data.audioUrls && data.audioUrls.length > 0) {
            setAudioUrl(data.audioUrls[0]);
          } else if (!isErTostik) {
            // er_tostik емес кітаптарды бірден қосымшаға жіберу
            setTimeout(() => {
              window.location.href = scheme;
              setTimeout(() => {
                window.location.href = APP_STORE_URL;
              }, 2000);
            }, 500);
          }
        } else {
          // Firestore-та жоқ болса, қосымшаға жіберу
          setTimeout(() => {
            window.location.href = scheme;
            setTimeout(() => {
              window.location.href = APP_STORE_URL;
            }, 2000);
          }, 500);
        }
      } catch (err) {
        console.error('Error fetching book data:', err);
      }
      setLoading(false);
    };

    fetchBookData();
  }, [id, isErTostik, scheme]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setIsBuffering(false);
      // Автоплей - аудио жүктелгенде автоматты ойнау
      audio.play().catch(() => {
        // Автoplay блок болса, isPlaying false қалады
        setIsPlaying(false);
      });
      setIsPlaying(true);
    };
    const handleEnded = () => setIsPlaying(false);
    const handleWaiting = () => setIsBuffering(true);
    const handleCanPlay = () => setIsBuffering(false);
    const handleError = () => setAudioError(true);

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('waiting', handleWaiting);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('waiting', handleWaiting);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('error', handleError);
    };
  }, [audioUrl]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const skip = (seconds) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Math.max(0, Math.min(audio.currentTime + seconds, duration));
    setCurrentTime(audio.currentTime);
  };

  const handleSeek = (e) => {
    const time = parseFloat(e.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const openApp = () => {
    // Қосымшаға жіберу - алдымен scheme, сосын App Store
    let appOpened = false;

    // Егер қосымша ашылса, visibilitychange арқылы білеміз
    const handleVisibility = () => {
      if (document.hidden) {
        appOpened = true;
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    // Safari'de scheme ашылғанда қате туралы хабарлама болмас үшін
    // iframe арқылы жасырын түрде ашуға болады
    if (navigator.userAgent.match(/Version\/[\d.]+.*Safari/i) && !navigator.userAgent.match(/Chrome/i)) {
      // Safari - iframe арқылы ашу
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = scheme;
      document.body.appendChild(iframe);
    } else {
      // Басқа браузерлер - тікелей жіберу
      window.location.href = scheme;
    }

    // 2.5 секундтан кейін App Store-ға жіберу (егер қосымша ашылмаса)
    setTimeout(() => {
      document.removeEventListener('visibilitychange', handleVisibility);
      if (!appOpened) {
        window.location.href = APP_STORE_URL;
      }
    }, 2500);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900">
        <div className="text-white/70 text-lg">Жүктелуде...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 p-4 font-sans">
      <div className="bg-white/6 backdrop-blur-xl rounded-3xl border border-white/10 p-8 md:p-10 max-w-sm w-full text-center text-white shadow-2xl">
        {bookData ? (
          <>
            {bookData.imageUrl && (
              <img
                src={bookData.imageUrl}
                alt={bookData.title}
                className="w-48 h-72 object-cover rounded-xl mb-5 mx-auto shadow-lg shadow-black/30"
              />
            )}

            <h1 className="text-2xl font-bold mb-2 leading-tight">
              {bookData.title || 'Кітап'}
            </h1>

            {bookData.author && (
              <div className="flex items-center justify-center gap-2 text-white/65 text-sm mb-3 italic">
                <FaUser className="text-xs" />
                <span>{bookData.author}</span>
              </div>
            )}

            {bookData.description && (
              <p className="text-white/70 text-sm leading-relaxed mb-3">
                {bookData.description}
              </p>
            )}

            {bookData.duration && (
              <div className="flex items-center justify-center gap-2 text-white/50 text-xs mb-4">
                <FaClock className="text-xs" />
                <span>Ұзақтығы: {bookData.duration}</span>
              </div>
            )}

            {/* Аудио плеер (тек er_tostik үшін) */}
            {isErTostik && audioUrl && (
              <div className="bg-white/10 rounded-2xl p-5 mb-6">
                <audio ref={audioRef} src={audioUrl} preload="metadata" onPlay={() => setIsPlaying(true)} onPause={() => setIsPlaying(false)} />

                {audioError ? (
                  <div className="text-red-400 text-sm py-4">
                    ⚠️ Аудио жүктелмеді. Интернетті тексеріңіз.
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-center gap-3 mb-3">
                      <button
                        onClick={() => skip(-10)}
                        className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-sm hover:bg-white/20 transition-colors"
                        title="10 сек артқа"
                      >
                        <FaBackward />
                      </button>

                      <button
                        onClick={togglePlay}
                        disabled={isBuffering}
                        className="w-16 h-16 rounded-full bg-gradient-to-r from-pink-500 to-orange-400 flex items-center justify-center text-3xl shadow-lg shadow-pink-500/40 hover:scale-105 transition-transform duration-200 disabled:opacity-50"
                      >
                        {isBuffering ? (
                          <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : isPlaying ? (
                          <FaPause />
                        ) : (
                          <FaPlay className="ml-1" />
                        )}
                      </button>

                      <button
                        onClick={() => skip(10)}
                        className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-sm hover:bg-white/20 transition-colors"
                        title="10 сек алға"
                      >
                        <FaForward />
                      </button>
                    </div>

                    <div className="flex justify-between text-xs text-white/70 font-mono mb-2">
                      <span>{formatTime(currentTime)}</span>
                      <span>{formatTime(duration)}</span>
                    </div>

                    <input
                      type="range"
                      min="0"
                      max={duration || 0}
                      value={currentTime}
                      onChange={handleSeek}
                      className="w-full h-1.5 bg-white/20 rounded-full appearance-none cursor-pointer accent-pink-500 hover:accent-pink-400 transition-all"
                      style={{
                        WebkitAppearance: 'none',
                        appearance: 'none',
                      }}
                    />
                  </>
                )}
              </div>
            )}

            <div className="h-px bg-white/10 my-7" />

            <p className="text-white/75 text-sm leading-relaxed mb-6">
              {isListen
                ? 'Кітапты тыңдауды жалғастыру үшін Esti.kz қосымшасын ашыңыз'
                : 'Кітап туралы толық ақпаратты қосымшада қараңыз'}
            </p>

            <button
              onClick={openApp}
              className="w-full py-4 bg-gradient-to-r from-pink-500 to-orange-400 text-white border-none rounded-xl text-base font-semibold cursor-pointer mb-3.5 hover:opacity-90 transition-opacity duration-200 shadow-lg shadow-pink-500/30"
            >
              <div className="flex items-center justify-center gap-2">
                <FaBook />
                <span>Қосымшада ашу</span>
              </div>
            </button>
          </>
        ) : (
          <>
            <div className="text-6xl mb-2">🎧</div>
            <h1 className="text-3xl font-bold mb-1.5 text-white">Esti.kz</h1>
            <p className="text-sm text-white/55 mb-7">Қазақ тіліндегі аудиокітаптар</p>

            <div className="h-px bg-white/10 my-7" />

            <p className="text-white/75 text-sm leading-relaxed mb-6">
              {isListen
                ? 'Кітапты тыңдауды жалғастыру үшін Esti.kz қосымшасын ашыңыз'
                : 'Кітап туралы толық ақпаратты қосымшада қараңыз'}
            </p>

            <button
              onClick={openApp}
              className="w-full py-4 bg-gradient-to-r from-pink-500 to-orange-400 text-white border-none rounded-xl text-base font-semibold cursor-pointer mb-3.5 hover:opacity-90 transition-opacity duration-200 shadow-lg shadow-pink-500/30"
            >
              <div className="flex items-center justify-center gap-2">
                <FaBook />
                <span>Қосымшада ашу</span>
              </div>
            </button>
          </>
        )}

        <a
          href={APP_STORE_URL}
          className="block text-white/50 text-xs no-underline py-2 hover:text-white/70 transition-colors duration-200"
        >
          <div className="flex items-center justify-center gap-2">
            <FaStore />
            <span>App Store-дан жүктеу</span>
          </div>
        </a>
      </div>
    </div>
  );
};

export default OpenInApp;
